import { Check, Plus, Scale } from 'lucide-react';
import { useState } from 'react';
import { useComparison } from '../hooks/ComparisonContext';
import { tx } from '../i18n';
import Notice from './Notice';

interface CompareButtonProps {
  propertyAddressId: string;
  analysisId: string | null;
  /** Fills the width of its column, for the summary beside the report. */
  block?: boolean;
}

/**
 * Add to comparison, with the counter alongside.
 *
 * <p>When the free limit refuses the add, the refusal is shown with the upgrade route rather than
 * silently doing nothing, which section 19 calls for explicitly.
 */
export default function CompareButton({ propertyAddressId, analysisId, block = true }: CompareButtonProps) {
  const { comparison, add, isShortlisted, limitReached, clearLimitFlag } = useComparison();
  const [busy, setBusy] = useState(false);

  const already = isShortlisted(propertyAddressId);
  const used = comparison?.usedFreeSlots ?? 0;
  const limit = comparison?.freeLimit ?? 3;

  const handleClick = async () => {
    setBusy(true);
    clearLimitFlag();
    await add(propertyAddressId, analysisId);
    setBusy(false);
  };

  return (
    <div className="stack stack--tight">
      <button
        type="button"
        className={block ? 'button button--secondary button--block' : 'button button--secondary'}
        onClick={handleClick}
        disabled={busy || already}
      >
        {already ? (
          <>
            <Check size={15} aria-hidden="true" />
            {tx('In your comparison', 'Karşılaştırmanızda',
    'W twoim porównaniu',
    'Na sua comparação',
    'En tu comparación')}
          </>
        ) : (
          <>
            <Plus size={15} aria-hidden="true" />
            {tx('Add to compare', 'Karşılaştırmaya ekle',
    'Dodaj do porównania',
    'Juntar à comparação',
    'Añadir a la comparación')}
          </>
        )}
      </button>

      {comparison?.paymentsRequired === false ? null : (
        <span className="tiny muted row" style={{ gap: 5 }}>
          <Scale size={13} aria-hidden="true" />
          {tx(`${used}/${limit} free slots used`, `${used}/${limit} ücretsiz hak kullanıldı`,
    `wykorzystano ${used}/${limit} bezpłatnych miejsc`,
    `${used}/${limit} lugares gratuitos usados`,
    `${used}/${limit} plazas gratuitas usadas`)}
        </span>
      )}

      {limitReached ? (
        <Notice tone="warning" title={tx(`You can compare ${limit} homes for free`, `${limit} konutu ücretsiz karşılaştırabilirsiniz`,
    `Możesz porównać ${limit} mieszkań bezpłatnie`,
    `Pode comparar ${limit} casas gratuitamente`,
    `Puedes comparar ${limit} viviendas gratis`)}>
          {tx(
            'Unlock one of the homes already in your comparison, or remove one, to add this. Unlocked homes never use a free slot.',
            'Bunu eklemek için karşılaştırmanızdaki konutlardan birinin kilidini açın ya da birini çıkarın. Kilidi açılmış konutlar ücretsiz hakkınızdan düşmez.',
    'Odblokuj jedno z mieszkań już w porównaniu albo usuń jedno, aby dodać to. Odblokowane mieszkania nigdy nie zajmują bezpłatnego miejsca.',
    'Desbloqueie uma das casas já na comparação, ou remova uma, para juntar esta. As casas desbloqueadas nunca ocupam um lugar gratuito.',
    'Desbloquea una de las viviendas que ya están en la comparación, o quita una, para añadir esta. Las viviendas desbloqueadas nunca ocupan una plaza gratuita.',
          )}
        </Notice>
      ) : null}
    </div>
  );
}
